import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Check, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { INDUSTRIES } from "@/data/industries";

const STEPS = [
  "Tell us about your business",
  "We’ll learn about your operations",
  "We’ll schedule a personalized demo",
];

const initialForm = {
  name: "",
  email: "",
  company: "",
  role: "",
  website: "",
  industry: "",
  otherIndustry: "",
};

const EASE_STANDARD = [0.16, 1, 0.3, 1];
const FADE_UP_TAG = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, ease: EASE_STANDARD } };
const FADE_UP_HEADLINE = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, ease: EASE_STANDARD } };
const FADE_UP_PANEL = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, ease: EASE_STANDARD } };
const SLIDE_IN_LEFT = { initial: { opacity: 0, x: -14 }, animate: { opacity: 1, x: 0 } };
const stepTransition = (i) => ({ delay: 0.3 + i * 0.12, duration: 0.7, ease: EASE_STANDARD });

export default function RequestDemoPage() {
  const { API } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Request a Demo — Nova Opsis";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const update = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({
      ...f,
      [key]: value,
      ...(key === "industry" && value !== "Other" ? { otherIndustry: "" } : {}),
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/demo-requests`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        role: form.role.trim() || null,
        website: form.website.trim() || null,
        industry: form.industry,
        other_industry: form.industry === "Other" ? form.otherIndustry.trim() || null : null,
      });
      setSubmittedEmail(form.email.trim());
      setForm(initialForm);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2" data-testid="request-demo-page">
      <div className="hidden lg:flex relative flex-col justify-between bg-[#020305] text-white p-12 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-nova-blue/20 blur-3xl" />
        <div className="absolute inset-0 bg-grid-navy opacity-[0.15]" />
        <div className="relative">
          <Link to="/" data-testid="brand-logo" className="inline-flex items-center">
            <img
              src="/nova-logo-lockup-dark.png"
              alt="NOVA OPSIS — A New Way Forward."
              className="h-20 lg:h-24 xl:h-28 w-auto select-none"
              draggable={false}
            />
          </Link>
        </div>
        <div className="relative">
          <motion.p
            {...FADE_UP_TAG}
            className="text-[11px] font-bold tracking-[0.28em] uppercase text-blue-400 mb-5"
          >
            Request Demo
          </motion.p>
          <motion.h2
            {...FADE_UP_HEADLINE}
            className="text-4xl xl:text-5xl font-extrabold tracking-tighter leading-[1.05]"
          >
            See a new way
            <br />
            <span className="text-blue-400">forward.</span>
          </motion.h2>
          <ul className="mt-10 space-y-4">
            {STEPS.map((step, i) => (
              <motion.li
                key={step}
                {...SLIDE_IN_LEFT}
                transition={stepTransition(i)}
                className="flex items-center gap-3 text-slate-300"
              >
                <span className="w-6 h-6 rounded-full bg-nova-blue/25 flex items-center justify-center shrink-0">
                  <Check size={12} strokeWidth={3} className="text-blue-300" />
                </span>
                <span className="text-sm font-medium">{step}</span>
              </motion.li>
            ))}
          </ul>
        </div>
        <p className="relative text-[10px] font-semibold tracking-[0.32em] text-blue-400">
          NOVA OPSIS
        </p>
      </div>

      <div className="flex items-center justify-center px-5 sm:px-10 py-12">
        <motion.div
          {...FADE_UP_PANEL}
          className="w-full max-w-md"
        >
          <Link
            to="/"
            data-testid="request-demo-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-nova-muted hover:text-nova-navy transition-colors mb-8"
          >
            <ArrowLeft size={15} />
            Back to site
          </Link>

          {submittedEmail ? (
            <div className="rounded-3xl border border-nova-pine/20 bg-nova-pinelight/40 p-8" data-testid="demo-request-success-message">
              <span className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-soft">
                <CheckCircle2 size={24} className="text-nova-pine" />
              </span>
              <h1 className="mt-6 text-3xl font-bold tracking-tighter text-nova-navy">Demo request received.</h1>
              <p className="mt-3 text-sm text-nova-slate leading-relaxed">
                Thanks for your interest in Nova Opsis. We'll contact you at{" "}
                <span className="font-semibold text-nova-navy">{submittedEmail}</span> to arrange your demo.
              </p>
              <p className="mt-4 text-xs text-nova-muted leading-relaxed">
                This does not create an account or organization.
              </p>
              <Link
                to="/"
                data-testid="demo-request-success-home-link"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-nova-blue px-6 py-3 text-sm font-semibold text-white hover:bg-nova-bluedark transition-colors duration-300"
              >
                Back to homepage
                <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-bold tracking-tighter text-nova-navy">Request a Nova Opsis demo</h1>
              <p className="mt-2 text-sm text-nova-slate">
                Tell us a little about your business and we'll get in touch to schedule a personalized demo.
              </p>

              <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="request-demo-form">
                <div>
                  <label htmlFor="request-name" className="block text-xs font-semibold text-nova-navy mb-1.5">
                    Your name
                  </label>
                  <input
                    id="request-name"
                    data-testid="request-name-input"
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Sarah Mitchell"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                  />
                </div>
                <div>
                  <label htmlFor="request-email" className="block text-xs font-semibold text-nova-navy mb-1.5">
                    Work email
                  </label>
                  <input
                    id="request-email"
                    data-testid="request-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@yourbusiness.com"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                  />
                </div>
                <div>
                  <label htmlFor="request-company" className="block text-xs font-semibold text-nova-navy mb-1.5">
                    Company name
                  </label>
                  <input
                    id="request-company"
                    data-testid="request-company-input"
                    type="text"
                    required
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Pine & Co. Studio"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                  />
                </div>

                <div>
                  <label htmlFor="request-industry" className="block text-xs font-semibold text-nova-navy mb-1.5">
                    Industry
                  </label>
                  <select
                    id="request-industry"
                    data-testid="request-industry-select"
                    required
                    value={form.industry}
                    onChange={update("industry")}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                  >
                    <option value="" disabled>Select your industry</option>
                    {INDUSTRIES.map((industry) => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                {form.industry === "Other" && (
                  <div>
                    <label htmlFor="request-other-industry" className="block text-xs font-semibold text-nova-navy mb-1.5">
                      Please specify your industry <span className="text-nova-muted font-normal">(optional)</span>
                    </label>
                    <input
                      id="request-other-industry"
                      data-testid="request-other-industry-input"
                      type="text"
                      value={form.otherIndustry}
                      onChange={update("otherIndustry")}
                      placeholder="e.g. Fleet services"
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                    />
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="request-role" className="block text-xs font-semibold text-nova-navy mb-1.5">
                      Your role <span className="text-nova-muted font-normal">(optional)</span>
                    </label>
                    <input
                      id="request-role"
                      data-testid="request-role-input"
                      type="text"
                      value={form.role}
                      onChange={update("role")}
                      placeholder="Owner"
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                    />
                  </div>
                  <div>
                    <label htmlFor="request-website" className="block text-xs font-semibold text-nova-navy mb-1.5">
                      Company website <span className="text-nova-muted font-normal">(optional)</span>
                    </label>
                    <input
                      id="request-website"
                      data-testid="request-website-input"
                      type="text"
                      value={form.website}
                      onChange={update("website")}
                      placeholder="yourbusiness.com"
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                    />
                  </div>
                </div>

                {error && (
                  <p data-testid="request-demo-error" className="text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                    {error}
                  </p>
                )}

                <motion.button
                  type="submit"
                  disabled={loading}
                  data-testid="request-demo-submit-btn"
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-nova-blue px-6 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgb(37,99,235,0.35)] hover:bg-nova-bluedark transition-colors duration-300 disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  Request Demo
                  {!loading && <ArrowRight size={15} />}
                </motion.button>
              </form>

              <p className="mt-6 text-sm text-nova-slate text-center">
                Already have access?{" "}
                <Link to="/login" data-testid="request-demo-signin-link" className="font-semibold text-nova-blue hover:text-nova-bluedark">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
